import React, { useMemo, useState, useEffect } from 'react';
import { Tabs, Tab, Paper, Box, Typography, CircularProgress, SvgIcon } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dashboard,
  AutoAwesome,
  PhotoLibrary,
  History,
  CreditCard,
  Person,
  Settings,
  CollectionsBookmark,
} from '@mui/icons-material';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import FlutterServicesAPI from '../../../services/flutterServices';

const CatalogIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <g transform="translate(2, 2)">
      <path fill="transparent" d="M18.6 2.59998H10.6M18.6 9.59998H10.6M18.6 16.6H10.6M0.6 0.599976H6.6V6.59998H0.6V0.599976ZM0.6 12.6H6.6V18.6H0.6V12.6Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square" />
    </g>
  </SvgIcon>
);

// Icon mapping for modules
const moduleIcons = {
  'Merchant Dashboard': <Dashboard />,
  'AI Generation': <AutoAwesome />,
  'Generation History': <History />,
  'Credits Management': <CreditCard />,
  'Merchant Profile': <Person />,
  'Gallery': <PhotoLibrary />,
  'Catalog Management': <CatalogIcon />,
  'Settings': <Settings />,
};

// Route mapping for modules (from backend route_name to frontend paths)
const routeMapping = {
  'dashboard': '/dashboard',
  'generate': '/generate',
  'gallery': '/gallery',
  'history': '/history',
  'credits': '/credits',
  'profile': '/profile',
  'web_catalogs': '/catalogs',
  '/dashboard': '/dashboard',   // Backward compatibility
  '/generate': '/generate',
  '/history': '/history',
  '/credits': '/credits',
  '/profile': '/profile',
  '/gallery': '/gallery',
  '/catalogs': '/catalogs',
};

// Label mapping for better display names
const labelMapping = {
  'Merchant Dashboard': 'Dashboard',
  'AI Generation': 'Create',
  'Generation History': 'History',
  'Credits Management': 'Tokens',
  'Merchant Profile': 'Profile',
  'Catalog Management': 'Catalogue',
};

// Mobile label mapping (shorter labels)
const mobileLabelMapping = {
  'Merchant Dashboard': 'Dashboard',
  'AI Generation': 'Create',
  'Generation History': 'History',
  'Credits Management': 'Tokens',
  'Merchant Profile': 'Profile',
  'Catalog Management': 'Catalogue',
};

const DynamicMerchantTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessibleModules, hasPermission, loading, error } = useMerchantPermissions();
  const isFlutterApp = FlutterServicesAPI.isFlutterApp();
  const showCreditTabInFlutter = process.env.REACT_APP_SHOW_CREDIT_TAB_IN_FLUTTER !== 'false';
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(isFlutterApp ? false : true);

  // Check auth visibility on mount
  useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ DynamicMerchantTabs: Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);

  // Generate tab configuration based on accessible modules
  const tabConfig = useMemo(() => {
    if (loading) return [];

    const accessibleModules = getAccessibleModules();

    // Convert modules to tab configuration
    const tabs = accessibleModules
      .filter(module => {
        // Include only the main navigation modules
        if (module.module_name === 'Merchant Dashboard' ||
          module.module_name === 'AI Generation' ||
          module.module_name === 'Gallery' ||
          module.module_name === 'Catalog Management' ||
          module.module_name === 'Generation History' ||
          module.module_name === 'Credits Management') {

          // Hide Credits Management tab completely in Flutter app unless env var is set
          if (module.module_name === 'Credits Management' && isFlutterApp && !showCreditTabInFlutter) {
            return false;
          }

          // Check if user has 'view' permission for this module (action-level check)
          const hasViewPermission = hasPermission(module.module_name, 'view');

          if (!hasViewPermission) {
            return false;
          }

          // Only include modules that have route mappings
          const backendRoute = module.route_name;
          return routeMapping[backendRoute] || routeMapping[`/${backendRoute}`] || module.route_name;
        }

        return false;
      })
      .map(module => {
        const backendRoute = module.route_name;
        const frontendRoute = routeMapping[backendRoute] || routeMapping[`/${backendRoute}`] || `/${backendRoute}`;

        const tabConfig = {
          label: labelMapping[module.module_name] || module.module_name,
          mobileLabel: mobileLabelMapping[module.module_name] || module.module_name,
          path: frontendRoute,
          icon: moduleIcons[module.module_name] || <Settings />,
          module: module.module_name,
          order: module.order_index || 999
        };

        return tabConfig;
      })
      .sort((a, b) => a.order - b.order); // Sort by order_index

    return tabs;
  }, [getAccessibleModules, hasPermission, loading, showWhatsAppSmsAuth]);

  const getActiveTab = (pathname) => {
    const found = tabConfig.findIndex(tab => pathname.startsWith(tab.path));
    return found === -1 ? 0 : found;
  };

  const activeTab = getActiveTab(location.pathname);

  const handleChange = (event, newValue) => {
    if (newValue < tabConfig.length) {
      navigate(tabConfig[newValue].path);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'block',
          mb: 3,
          mt: 4,
          pt: 2,
          pb: 2,
          borderRadius: 2,
          bgcolor: 'rgba(248, 250, 252, 1)',
          textAlign: 'center'
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Loading navigation...
        </Typography>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'block',
          mb: 3,
          mt: 4,
          pt: 2,
          pb: 2,
          borderRadius: 2,
          bgcolor: 'rgba(248, 250, 252, 1)',
          textAlign: 'center'
        }}
      >
        <Typography variant="body2" color="error">
          Error loading navigation. Using default tabs.
        </Typography>
      </Paper>
    );
  }

  // No tabs available
  if (!tabConfig || tabConfig.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          display: 'block',
          mb: 3,
          mt: 4,
          pt: 2,
          pb: 2,
          borderRadius: 2,
          bgcolor: 'rgba(248, 250, 252, 1)',
          textAlign: 'center'
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No accessible modules available.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* SVG Gradient Definition for Logo Colors */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="glammify-logo-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#7c3aed', stopOpacity: 1 }} />
            <stop offset="15%" style={{ stopColor: '#9333ea', stopOpacity: 1 }} />
            <stop offset="35%" style={{ stopColor: '#f43ba8', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#ff6ba9', stopOpacity: 1 }} />
            <stop offset="70%" style={{ stopColor: '#ff8e3c', stopOpacity: 1 }} />
            <stop offset="85%" style={{ stopColor: '#ffa500', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#fdda0d', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>

      {/* Desktop Tabs - Sticky at top */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          mb: 3,
          mt: 4,
          pt: 1,
          pb: 1,
          pl: 1,
          pr: 1,
          borderRadius: 2,
          bgcolor: 'rgba(255, 255, 255, 1)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChange}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{ minHeight: 30 }}
        >
          {tabConfig.map((tab, idx) => (
            <Tab
              key={tab.path}
              label={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {React.cloneElement(tab.icon, {
                    sx: {
                      mr: 1,
                      fontSize: 20,
                      fill: (tab.module === 'AI Generation' && activeTab !== idx) ? 'url(#glammify-logo-gradient)' : 'currentColor',
                    }
                  })}
                  {tab.label}
                </span>
              }
              sx={{
                minHeight: 40,
                fontWeight: 600,
                fontSize: 15,
                textTransform: 'capitalize',
                bgcolor: activeTab === idx ? '#6318C6' : 'transparent',
                color: activeTab === idx ? 'white' : '#374151',
                boxShadow: activeTab === idx ? '0 2px 8px 0 rgba(99, 24, 198, 0.3)' : 'none',
                borderRadius: 2,
                mx: 0,
                transition: 'all 0.3s ease',
                zIndex: activeTab === idx ? 1 : 0,
                '&:hover': {
                  bgcolor: activeTab === idx ? '#6318C6' : 'rgba(241, 245, 249, 0.8)',
                  transform: activeTab === idx ? 'translateY(-1px)' : 'none',
                  boxShadow: activeTab === idx ? '0 4px 12px 0 rgba(99, 24, 198, 0.4)' : 'none',
                },
                '& .MuiSvgIcon-root': {
                  color: activeTab === idx ? 'white' : (tab.module === 'AI Generation' ? 'transparent' : '#374151'),
                },
                '& .MuiTab-label': {
                  color: activeTab === idx ? 'white' : '#374151',
                },
                '& span': {
                  color: activeTab === idx ? 'white' : '#374151',
                },
              }}
              disableRipple
            />
          ))}
        </Tabs>
      </Paper>

      {/* Mobile/Tablet Tabs - Fixed at bottom */}
      <Paper
        id="mobile-merchant-tabs"
        elevation={3}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          borderRadius: 0, // Square corners
          bgcolor: 'white',
          borderTop: '1px solid #e2e8f0',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between', // Equal spacing
            alignItems: 'center',
            width: '100%',
            px: 1,
            py: 0, // Remove top/bottom padding
            pb: 0, // Remove bottom padding
          }}
        >
          {tabConfig.map((tab, idx) => (
            <Box
              key={tab.path}
              onClick={() => navigate(tab.path)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: `${100 / tabConfig.length}%`, // Dynamic width based on number of tabs
                py: 1.5, // Reduced padding from 2 to 1.5
                px: 1,
                cursor: 'pointer',
                borderRadius: 0, // Square corners
                transition: 'all 0.3s ease',
                position: 'relative',
                minHeight: '45px', // Reduced height from 50px to 45px
                bgcolor: activeTab === idx ? 'rgba(248, 250, 252, 1)' : 'transparent', // Lighter background for selected
                transform: activeTab === idx ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  bgcolor: activeTab === idx ? 'rgba(241, 245, 249, 1)' : 'rgba(100, 116, 139, 0.08)',
                  transform: activeTab === idx ? 'scale(1.05)' : 'scale(1.02)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              {/* Active top indicator */}
              {activeTab === idx && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '30px',
                    height: '3px',
                    borderRadius: 0, // Square indicator
                    bgcolor: '#6318C6',
                  }}
                />
              )}

              {/* Icon with enhanced active state */}
              <Box
                sx={{
                  mb: 0.5, // Reduced spacing between icon and text from 0 to 0.5
                  p: 0,
                  borderRadius: 0, // Square icon container
                  bgcolor: 'transparent', // No background on icon container
                  transition: 'all 0.3s ease',
                }}
              >
                {React.cloneElement(tab.icon, {
                  sx: {
                    fontSize: 20, // Reduced from 24 to 20
                    fill: (tab.module === 'AI Generation' && activeTab !== idx) ? 'url(#glammify-logo-gradient)' : 'currentColor',
                    color: activeTab === idx ? '#6318C6' : (tab.module === 'AI Generation' ? 'transparent' : '#9CA3AF'),
                    transition: 'all 0.3s ease',
                  }
                })}
              </Box>

              {/* Text with enhanced styling */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: 9, sm: 11 }, // Increased by 1px from 9/10 to 10/11
                  fontWeight: 500, // Consistent font weight for all tabs
                  color: activeTab === idx ? '#6318C6' : '#9CA3AF', // Keep purple for active text
                  textAlign: 'center',
                  lineHeight: 1.2,
                  letterSpacing: '0.5px', // Add letter spacing for better readability
                  transition: 'all 0.3s ease',
                }}
              >
                {tab.mobileLabel}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Spacer for mobile tabs to add margin top for desktop when tabs are not at bottom */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 4 }} />
    </>
  );
};

export default DynamicMerchantTabs; 